<template>
  <b-card>
    <b-row>
      <b-col md="4" class="pb-1">
        <b-form-group>
          <b-form-input
            v-model="searchQuery"
            :placeholder="$t('message.search')"
            type="search"
            class="d-inline-block"
          />
        </b-form-group>
      </b-col>

      <!-- Espacio libre a propósito, para alinear la fila de abajo con Buscar/Filtrar por área
           en la primera columna y los botones/Filtrar por privilegio en la tercera. -->
      <b-col md="4" class="pb-1" />

      <b-col md="4" class="pb-1 d-flex align-items-start">
        <b-button
          v-if="role === 'consultant'"
          size="sm"
          variant="outline-info"
          class="flex-fill mr-1 d-flex align-items-center justify-content-center worker-list-action-btn"
          @click="isSupervisorsAssignationSidebarActive = true"
        >
          <feather-icon
            icon="Edit2Icon"
            size="13"
            class="mr-50 flex-shrink-0"
          />
          {{ $t('assignSupervisors') }}
        </b-button>

        <b-button
          v-if="hasPermission('organization-workers', 'create')"
          size="sm"
          variant="outline-primary"
          class="flex-fill d-flex align-items-center justify-content-center worker-list-action-btn"
          :to="{ name: 'organization-worker-add' }"
        >
          <feather-icon
            icon="PlusIcon"
            size="13"
            class="mr-50 flex-shrink-0"
          />
          {{ $t('message.Add_Worker') }}
        </b-button>
      </b-col>

      <b-col>
        <filters
          :locationFilterShow="true"
          :roleFilterShow="true"
          :privilegeFilterShow="true"
          @changeFilter="updateFilter"
        />
      </b-col>

      <b-col cols="12" class="pb-1">
        <b-alert show variant="secondary" class="px-2 py-1 mb-0 small">
          <feather-icon icon="InfoIcon" class="mr-50" />
          {{ $t('message.worker_role_vs_privilege_hint') }}
        </b-alert>
      </b-col>

      <b-col cols="12">
        <b-table
          ref="refWorkerListTable"
          :items="workers"
          hover
          responsive
          :fields="tableColumns"
          primary-key="id"
          :sort-by.sync="sortBy"
          show-empty=""
          :empty-text="isLoading ? $t('message.loading') : $t('message.no_records')"
          :sort-desc.sync="isSortDirDesc"
        >
          <template #cell(name)="data">
            {{ data.value || '-' }}
          </template>

          <template #cell(email)="data">
            {{ data.value || '-' }}
          </template>

          <template #cell(companyData)="data">
            <span v-if="!data.item.companyData || !data.item.companyData.length">-</span>
            <template v-else>
              <b-badge
                v-for="(company, index) in data.item.companyData.slice(0, 2)"
                :key="index"
                class="mr-1 mb-1"
                variant="light-info"
              >
                {{ truncateWords(company.name) }}
              </b-badge>

              <feather-icon
                v-if="data.item.companyData.length > 2"
                badge-classes="custom_badge_settings"
                :badge="`+${data.item.companyData.length - 2}`"
                icon=""
                size="1"
              />
            </template>
          </template>

          <template #cell(rolesData)="data">
            <span v-if="!data.item.rolesData.length">-</span>
            <template v-else>
              <b-badge
                v-for="(role, index) in data.item.rolesData.slice(0, 2)"
                :key="index"
                class="mr-1 mb-1"
                variant="light-secondary"
              >
                {{ truncateWords(role.name) }}
              </b-badge>

              <feather-icon
                v-if="data.item.rolesData.length > 2"
                badge-classes="custom_badge_settings"
                :badge="`+${data.item.rolesData.length - 2}`"
                icon=""
                size="1"
              />
            </template>
          </template>

          <template #cell(privileges)="data">
            <b-badge v-if="data.item.privileges" variant="light-primary">
              {{ privilegeLabel(data.item.privileges) }}
            </b-badge>
            <span v-else>-</span>
          </template>

          <template #cell(hasCustomPermissions)="data">
            <b-badge :variant="data.item.hasCustomPermissions ? 'light-success' : 'light-secondary'">
              {{ data.item.hasCustomPermissions ? $t('message.yes') : $t('message.no') }}
            </b-badge>
          </template>

          <template #cell(actions)="data">
            <div class="flex flex-column">
              <!-- <UserButton @clicked="router.push({name: 'apps-users-view', params: { id: data.item._id }})"/> -->
              <EditButton v-if="hasPermission('organization-workers', 'edit')" @clicked="router.push({name: 'organization-worker-edit', params: { id: data.item._id }})"/>
              <DeleteButton v-if="hasPermission('organization-workers', 'delete')" v-b-modal="`modal ${data.item._id}`"/>
              <b-modal
                :id="`modal ${data.item._id}`"
                centered
                no-close-on-backdrop
                modal-class="modal-danger"
                ok-variant="danger"
                cancel-variant="outline-secondary"
                :title="$t('message.confirm_action')"
                :ok-title="$t('message.delete')"
                :cancel-title="$t('message.cancel')"
                @ok="deleteWorker(data.item._id, deletedSuccess)"
              >
                {{ $t('message.confirm_delete_worker') }}
              </b-modal>
            </div>
          </template>
        </b-table>
      </b-col>

      <b-col
        cols="12"
        class="d-flex justify-content-between flex-wrap"
      >
        <div class="mb-1">
          <b-form-group class="mb-0">
            <label class="d-inline-block text-sm-left mr-50">{{ $t('message.per_page') }}</label>
            <b-form-select
              id="perPageSelect"
              v-model="perPage"
              size="sm"
              :options="[5, 10, 15, 20, 25, 30, 40, 50, 100]"
              class="w-50"
            />
          </b-form-group>
        </div>

        <div class="mb-1">
          <span class="text-muted">{{ $tc('message.paginationText', 0, { from: `${((currentPage * perPage) - perPage) + (workers.length ? 1 : 0)}`, to: `${workers.length + ((currentPage * perPage) - perPage)}`, total: `${totalWorkers}` }) }}</span>
        </div>

        <b-pagination
          v-model="currentPage"
          :total-rows="totalWorkers"
          :per-page="perPage"
          first-number=""
          last-number=""
          prev-class="prev-item"
          next-class="next-item"
        />
      </b-col>
    </b-row>

    <b-button v-if="hasPermission('organization-workers', 'export')" :disabled="!workersTotal.length">
      <json-excel :data="workersTotal" :fields="excelFields">{{ $t('download_xlsx') }}</json-excel>
    </b-button>

    <!-- Sidebar Overlay: Assign Supervisors -->
    <div
      class="body-content-overlay"
      :class="{'show': isCalendarOverlaySidebarActive}"
      @click="isCalendarOverlaySidebarActive = false"
    />
    <supervisors-assignation
      v-model="isSupervisorsAssignationSidebarActive"
    />
  </b-card>
</template>

<script>
import {
  BBadge, BPagination, BTable, BModal, VBModal, BAlert,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { onUnmounted, onMounted, ref } from '@vue/composition-api'
import store from '@/store'
import JsonExcel from "vue-json-excel";
// eslint-disable-next-line import/no-cycle
import { useRouter } from '@core/utils/utils'
import EditButton from '@/views/components/Shared/Buttons/EditButton.vue'
import UserButton from '@/views/components/Shared/Buttons/UserButton.vue'
import DeleteButton from '@/views/components/Shared/Buttons/DeleteButton.vue'

import useNotifications from '@/composables/useNotifications'
import useWorkersList from './useWorkerList'

import workerStoreModule from '../workerStoreModule'
import i18n from '@/libs/i18n/index.js'
import Filters from "@/views/organization/Filters.vue";
import SupervisorsAssignation from '../supervisors-assignation/SupervisorsAssignation.vue'
import { hasPermission } from '@/composables/usePermission'

export default {
  components: {
    BTable,
    BBadge,
    BAlert,
    BPagination,
    EditButton,
    UserButton,
    DeleteButton,
    // eslint-disable-next-line vue/no-unused-components
    vSelect,
    Filters,
    BModal,
    SupervisorsAssignation,
    JsonExcel,
  },
  directives: {
    'b-modal': VBModal,
  },
  data() {
    return {
      items: [],
    }
  },
  setup() {
    const { showSuccessMessage, showErrorMessage } = useNotifications()
    const ORGANIZATION_WORKER_STORE_MODULE_NAME = 'organization-worker'
    const { router } = useRouter()
    const {
      fetchWorkers,
      tableColumns,
      perPage,
      currentPage,
      totalWorkers,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refetchData,
      deleteWorker,
      refWorkerListTable,
      workers,
      locationQuery,
      locations,
      // getLocations,
      locationFilter,
      roleFilter,
      privilegeFilter,
      companyFilter,
      isLoading,
      role,
      excelFields,
      workersTotal
    } = useWorkersList()
    const isSupervisorsAssignationSidebarActive = ref(false)
    const isCalendarOverlaySidebarActive = ref(false)

    // Register module
    if (!store.hasModule(ORGANIZATION_WORKER_STORE_MODULE_NAME)) {
      store.registerModule(
        ORGANIZATION_WORKER_STORE_MODULE_NAME,
        workerStoreModule,
      )
    }

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(ORGANIZATION_WORKER_STORE_MODULE_NAME)) { store.unregisterModule(ORGANIZATION_WORKER_STORE_MODULE_NAME) }
    })

    function updateFilter(data) {
      locationFilter.value = data.locationFilter;
      roleFilter.value = data.roleFilter;
      privilegeFilter.value = data.privilegeFilter;
      companyFilter.value = data.companyFilter;

      fetchWorkers();
    }

    onMounted(async () => {
      fetchWorkers()
      // getLocations()
    })

    const deletedSuccess = success => {
      if (success) {
        fetchWorkers()
        showSuccessMessage(i18n.t('message.worker_remove'))
      } else {
        showErrorMessage(i18n.t('message.something_went_wrong'))
      }
    }

    // worker.privileges guarda el valor en minúscula (consultant/manager/supervisor/employee)
    // pero las labels ya traducidas viven bajo la key capitalizada (Consultant/Manager/...).
    const PRIVILEGE_LABEL_KEYS = {
      consultant: 'Consultant', manager: 'Manager', supervisor: 'Supervisor', employee: 'Employee',
    }
    const privilegeLabel = privileges => i18n.t(`userRoles.${PRIVILEGE_LABEL_KEYS[privileges] || privileges}`)

    return {
      router,
      fetchWorkers,
      tableColumns,
      privilegeLabel,
      perPage,
      currentPage,
      totalWorkers,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refetchData,
      deleteWorker,
      deletedSuccess,
      refWorkerListTable,
      workers,
      locationQuery,
      locitems: locations,
      // getLocations,
      updateFilter,
      companyFilter,
      isLoading,
      isSupervisorsAssignationSidebarActive,
      isCalendarOverlaySidebarActive,
      role,
      excelFields,
      workersTotal,
      hasPermission,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

// Título de 2 líneas ("Privilegio\npersonalizado") para no ensanchar la columna por el
// texto del encabezado — el ancho real de la columna lo termina definiendo el contenido
// (badge Sí/No), no el título.
::v-deep .worker-custom-permissions-header {
  white-space: pre-line;
  text-align: center;
  line-height: 1.2;
}

.process-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}

.form-group {
  label {
    font-size: 0.85rem;
    font-weight: 400;
  }
}

// Altura mínima igual al input de búsqueda ($input-height del theme). Antes forzaba
// `white-space: nowrap` + `height` fija para mantener el texto en una sola línea, pero con
// dos botones flex-fill en una columna angosta (md="4") eso hacía que el botón se estirara
// más allá del contenedor en vez de encogerse — el texto no tenía forma de ceder. Con
// min-height + wrap habilitado, el botón crece en alto (texto en dos líneas) en pantallas
// angostas en vez de desbordar en ancho. Fuente/padding/ícono más chicos (ver `size="sm"` +
// `size="13"` del ícono en el template) para que el texto entre en una sola línea en la
// mayoría de los anchos, en vez de depender del wrap.
.worker-list-action-btn {
  min-height: 2.714rem;
  padding-top: 0.4rem;
  padding-bottom: 0.4rem;
  padding-left: 0.6rem;
  padding-right: 0.6rem;
  font-size: 0.8rem;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
